import React from 'react';
import styled from 'styled-components';
import { Button, IconButton } from '../../../Button';
import VipIcon from '../../../../Icons/VipIcons';
import ProfileImage from '../../../ProfileImage';
import { CARDS } from '../../../../../constants/staticTexts';

// Base card container
const UserCardContainer = styled.div`
  background: ${({theme}) => theme.colors.bg2};
  border: 1px solid ${({theme}) => theme.colors.border};
  border-radius: ${({theme}) => theme.borderRadius.md};
  padding: ${({$size}) => {
    switch ($size) {
      case 'large': return '24px';
      case 'medium': return '16px';
      case 'compact': return '12px';
      default: return '16px';
    }
  }};
  
  /* Reduce padding on mobile for compact cards */
  @media (max-width: 768px) {
    padding: ${({$size}) => {
      switch ($size) {
        case 'large': return '20px';
        case 'medium': return '12px';
        case 'compact': return '6px';
        default: return '12px';
      }
    }};
  }
  transition: all 0.2s ease;
  cursor: ${({$interactive}) => $interactive ? 'pointer' : 'default'};
  position: relative;
  display: flex;
  flex-direction: ${({$layout}) => $layout === 'horizontal' ? 'row' : 'column'};
  align-items: ${({$layout}) => $layout === 'horizontal' ? 'center' : 'flex-start'};
  justify-content: flex-start; /* Ensure items start from the beginning, no space-between */
  gap: ${({$size, $layout}) => {
    if ($layout === 'horizontal') {
      switch ($size) {
        case 'large': return '20px';
        case 'medium': return '16px';
        case 'compact': return '12px';
        default: return '16px';
      }
    }
    return '16px';
  }};
  
  /* Reduce gap on mobile when horizontal layout becomes vertical */
  @media (max-width: 768px) {
    gap: ${({$size, $layout}) => {
      if ($layout === 'horizontal') {
        switch ($size) {
          case 'large': return '8px';
          case 'medium': return '6px';
          case 'compact': return '8px'; /* Increased for better spacing on mobile */
          default: return '6px';
        }
      }
      return '16px';
    }};
  }
  
  &:hover {
    ${({$interactive, theme}) => $interactive && `
      background: ${theme.colors.primaryGradientLight};
      border-color: ${theme.colors.primaryPink}40;
      transform: translateY(-2px);
      box-shadow: ${theme.shadows.md};
    `}
  }
  
  ${({$size, $layout}) => {
    if ($layout === 'horizontal') {
      switch ($size) {
        case 'large':
          return `
            min-height: 120px;
            @media (max-width: 768px) {
              flex-direction: column;
              text-align: center;
              min-height: auto;
            }
          `;
        case 'medium':
          return `
            min-height: 80px;
            @media (max-width: 768px) {
              min-height: 70px;
            }
          `;
        case 'compact':
          return `
            min-height: 60px;
            @media (max-width: 768px) {
              min-height: 50px;
            }
          `;
        default:
          return '';
      }
    } else {
      switch ($size) {
        case 'large':
          return `
            min-height: 280px;
            text-align: center;
            @media (max-width: 768px) {
              min-height: 240px;
            }
          `;
        case 'medium':
          return `
            min-height: 200px;
            text-align: center;
          `;
        case 'compact':
          return `
            min-height: 140px;
            text-align: center;
          `;
        default:
          return '';
      }
    }
  }}
`;

// Avatar container with status indicators
const AvatarContainer = styled.div`
  position: relative;
  flex-shrink: 0;
  
  ${({$layout}) => $layout === 'vertical' && `
    align-self: center;
    margin-bottom: 12px;
  `}
  
  /* Remove margin-bottom on mobile for horizontal layouts that become vertical */
  @media (max-width: 768px) {
    ${({$layout}) => $layout === 'horizontal' && `
      margin-bottom: 0;
    `}
  }
`;

// Status indicator (online, verified, etc.)
const StatusIndicator = styled.div`
  position: absolute;
  bottom: ${({$avatarSize}) => {
    switch ($avatarSize) {
      case 'large': return '4px';
      case 'medium': return '2px';
      case 'compact': return '0px';
      default: return '2px';
    }
  }};
  right: ${({$avatarSize}) => {
    switch ($avatarSize) {
      case 'large': return '4px';
      case 'medium': return '2px';
      case 'compact': return '0px';
      default: return '2px';
    }
  }};
  width: ${({$avatarSize}) => {
    switch ($avatarSize) {
      case 'large': return '24px';
      case 'medium': return '20px';
      case 'compact': return '16px';
      default: return '20px';
    }
  }};
  height: ${({$avatarSize}) => {
    switch ($avatarSize) {
      case 'large': return '24px';
      case 'medium': return '20px';
      case 'compact': return '16px';
      default: return '20px';
    }
  }};
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  svg {
    color: ${({$variant, theme}) => {
      switch ($variant) {
        case 'verified': return theme.colors.primaryPink;
        case 'online': return theme.colors.success;
        case 'creator': return theme.colors.info;
        default: return theme.colors.textSecondary;
      }
    }};
  }
`;

// User info container
const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({$size}) => $size === 'compact' ? '1px' : '4px'};
  
  /* Even tighter gap on mobile for compact */
  @media (max-width: 768px) {
    gap: ${({$size}) => $size === 'compact' ? '0px' : '2px'};
  }
  
  ${({$layout}) => $layout === 'horizontal' && `
    @media (max-width: 768px) {
      text-align: left;
    }
  `}
`;

// User name
const UserName = styled.h3`
  font-size: ${({$size, theme}) => {
    switch ($size) {
      case 'large': return theme.fontSizes.xl;
      case 'medium': return theme.fontSizes.lg;
      case 'compact': return theme.fontSizes.md;
      default: return theme.fontSizes.lg;
    }
  }};
  font-weight: 600;
  color: ${({theme}) => theme.colors.text};
  margin: 0;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Username
const Username = styled.p`
  font-size: ${({$size, theme}) => {
    switch ($size) {
      case 'large': return theme.fontSizes.md;
      case 'medium': return theme.fontSizes.sm;
      case 'compact': return theme.fontSizes.xs;
      default: return theme.fontSizes.sm;
    }
  }};
  color: ${({theme}) => theme.colors.textSecondary};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Bio text
const Bio = styled.p`
  font-size: ${({$size, theme}) => {
    switch ($size) {
      case 'large': return theme.fontSizes.sm;
      case 'medium': return theme.fontSizes.xs;
      default: return theme.fontSizes.xs;
    }
  }};
  color: ${({theme}) => theme.colors.textSecondary};
  margin: 8px 0 0 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${({$size}) => {
    switch ($size) {
      case 'large': return 3;
      case 'medium': return 2;
      default: return 1;
    }
  }};
  -webkit-box-orient: vertical;
  
  ${({$size}) => $size === 'compact' && `
    @media (max-width: 768px) {
      display: none;
    }
  `}
`;

// Badges container
const BadgesContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0;
  flex-wrap: wrap;
  
  ${({$layout}) => $layout === 'vertical' && `
    justify-content: center;
  `}
`;

// Badge component
const Badge = styled.span`
  background: ${({$variant, theme}) => {
    switch ($variant) {
      case 'creator': return theme.colors.primaryPink;
      case 'verified': return theme.colors.info;
      case 'premium': return theme.colors.warning;
      case 'new': return theme.colors.success;
      default: return theme.colors.textSecondary;
    }
  }};
  color: white;
  font-size: ${({$size}) => {
    switch ($size) {
      case 'large': return '10px';
      case 'medium': return '9px';
      case 'compact': return '8px';
      default: return '9px';
    }
  }};
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Stats container
const StatsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({$size}) => {
    switch ($size) {
      case 'large': return '20px';
      case 'medium': return '16px';
      case 'compact': return '12px';
      default: return '16px';
    }
  }};
  margin: 8px 0;
  flex-wrap: wrap;
  
  ${({$layout}) => $layout === 'vertical' && `
    justify-content: center;
  `}
  
  ${({$size}) => $size === 'compact' && `
    @media (max-width: 768px) {
      display: none;
    }
  `}
`;

// Individual stat
const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  
  ${({$layout}) => $layout === 'horizontal' && `
    flex-direction: row;
    gap: 4px;
  `}
`;

const StatValue = styled.span`
  font-size: ${({$size, theme}) => {
    switch ($size) {
      case 'large': return theme.fontSizes.lg;
      case 'medium': return theme.fontSizes.md;
      case 'compact': return theme.fontSizes.sm;
      default: return theme.fontSizes.md;
    }
  }};
  font-weight: 600;
  color: ${({theme}) => theme.colors.text};
`;

const StatLabel = styled.span`
  font-size: ${({$size, theme}) => {
    switch ($size) {
      case 'large': return theme.fontSizes.xs;
      case 'medium': return theme.fontSizes.xs;
      case 'compact': return theme.fontSizes.xs;
      default: return theme.fontSizes.xs;
    }
  }};
  color: ${({theme}) => theme.colors.textSecondary};
  text-transform: lowercase;
`;

// Actions container
const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  flex-wrap: wrap;
  
  ${({$layout}) => $layout === 'vertical' && `
    justify-content: center;
    margin-top: 16px;
  `}
  
  ${({$size}) => $size === 'compact' && `
    @media (max-width: 768px) {
      display: none;
    }
  `}
`;

// Action button
const ACTION_SIZES = { compact: 'small', medium: undefined, large: 'large' };

const ActionButton = styled(Button).attrs(({ $size }) => ({
  $size: ACTION_SIZES[$size],
}))``;

// Context menu button (for compact cards)
const ContextMenuButton = styled(IconButton).attrs({ $size: 'small' })`
  position: absolute;
  top: 4px;
  right: 4px;
  color: ${({theme}) => theme.colors.textSecondary};

  &:hover:not(:disabled) {
    color: ${({theme}) => theme.colors.text};
  }
`;

// Checkbox container (for compact cards with selection)
const CheckboxContainer = styled.div`
  cursor: pointer;
  padding: 2px;
  border-radius: ${({theme}) => theme.borderRadius.sm};
  transition: all 0.2s ease;
  background: ${({$selected, theme}) => $selected ? theme.colors.primary : 'white'};
  color: ${({$selected, theme}) => $selected ? 'white' : theme.colors.textSecondary};
  border: 1px solid ${({$selected, theme}) => $selected ? theme.colors.primary : theme.colors.border};
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    border-color: ${({theme}) => theme.colors.primary};
    background: ${({$selected, theme}) => $selected ? theme.colors.primaryDark : theme.colors.primaryGradientLight};
  }
`;

const UserCard = ({
  user,
  size = 'medium',
  layout = 'vertical',
  interactive = true,
  showBio = true,
  showStats = true,
  showActions = true,
  selected = false,
  onSelect,
  actions = [],
  onClick,
  onActionClick,
  className,
  ...props
}) => {
  const getAvatarSize = () => {
    switch (size) {
      case 'large': return layout === 'horizontal' ? '80px' : '120px';
      case 'medium': return layout === 'horizontal' ? '60px' : '80px';
      case 'compact': return '40px';
      default: return '60px';
    }
  };

  const getStatusIcon = () => {
    if (user.verified) return { name: 'VerifiedBadge', variant: 'verified' };
    if (user.isCreator) return { name: 'Star', variant: 'creator' };
    if (user.isOnline) return { name: 'Circle', variant: 'online' };
    return null;
  };

  const handleCheckboxClick = (event) => {
    event.stopPropagation();
    if (onSelect) {
      onSelect(user);
    }
  };

  const handleClick = () => {
    // For compact cards with selection, clicking anywhere should trigger selection
    if (size === 'compact' && layout === 'horizontal' && onSelect) {
      onSelect(user);
    } else if (interactive && onClick) {
      onClick(user);
    }
  };

  const handleActionClick = (action, event) => {
    event.stopPropagation();
    if (onActionClick) {
      onActionClick(action, user);
    }
  };

  const statusIcon = getStatusIcon();

  return (
    <UserCardContainer
      $size={size}
      $layout={layout}
      $interactive={interactive}
      onClick={handleClick}
      className={className}
      {...props}
    >
      {/* Compact horizontal layout with checkbox (like ListCard) */}
      {size === 'compact' && layout === 'horizontal' ? (
        <>
          <AvatarContainer $layout={layout}>
            <ProfileImage
              user={user}
              size={getAvatarSize()}
            />
          </AvatarContainer>
          
          <UserInfo $layout={layout} style={{ flex: 1, marginRight: '12px' }}>
            <UserName $size={size} style={{ margin: 0, lineHeight: 1.2 }}>
              {user.fullName || user.displayName || CARDS.USER_UNKNOWN}
            </UserName>
            <Username $size={size} style={{ margin: 0 }}>
              {CARDS.USER_USERNAME_PREFIX}{user.username || CARDS.USER_DEFAULT_USERNAME}
            </Username>
          </UserInfo>
          
          <CheckboxContainer
            $selected={selected}
            onClick={handleCheckboxClick}
          >
            {selected && (
              <VipIcon 
                name="Checkmark" 
                size={12} 
              />
            )}
          </CheckboxContainer>
        </>
      ) : (
        <>
          {/* Standard layout for other sizes */}
          {size === 'compact' && actions.length > 0 && (
            <ContextMenuButton aria-label="More actions" onClick={(e) => handleActionClick('menu', e)}>
              <VipIcon name="MoreHorizontal" size={16} />
            </ContextMenuButton>
          )}
          
          <AvatarContainer $layout={layout}>
            <ProfileImage
              user={user}
              size={getAvatarSize()}
            />
            {statusIcon && (
              <StatusIndicator 
                $avatarSize={size} 
                $variant={statusIcon.variant}
              >
                <VipIcon 
                  name={statusIcon.name} 
                  size={size === 'large' ? 16 : size === 'medium' ? 14 : 12} 
                />
              </StatusIndicator>
            )}
          </AvatarContainer>
      
                
          <UserInfo $layout={layout}>
            <UserName $size={size}>
              {user.fullName || user.displayName || CARDS.USER_UNKNOWN}
            </UserName>
            <Username $size={size}>
              {CARDS.USER_USERNAME_PREFIX}{user.username || CARDS.USER_DEFAULT_USERNAME}
            </Username>
            
            <BadgesContainer $layout={layout}>
              {user.verified && (
                <Badge $variant="verified" $size={size}>{CARDS.USER_BADGE_VERIFIED}</Badge>
              )}
            </BadgesContainer>
            
            {showBio && user.bio && size !== 'compact' && (
              <Bio $size={size}>{user.bio}</Bio>
            )}
            
            {showStats && (user.followersCount !== undefined || user.postsCount !== undefined) && (
              <StatsContainer $size={size} $layout={layout}>
                {user.followersCount !== undefined && (
                  <Stat $layout={layout}>
                    <StatValue $size={size}>
                      {user.followersCount?.toLocaleString() || 0}
                    </StatValue>
                    <StatLabel $size={size}>{CARDS.USER_STAT_FOLLOWERS}</StatLabel>
                  </Stat>
                )}
                {user.postsCount !== undefined && (
                  <Stat $layout={layout}>
                    <StatValue $size={size}>
                      {user.postsCount?.toLocaleString() || 0}
                    </StatValue>
                    <StatLabel $size={size}>{CARDS.USER_STAT_POSTS}</StatLabel>
                  </Stat>
                )}
                {user.subscriptionsCount !== undefined && size === 'large' && (
                  <Stat $layout={layout}>
                    <StatValue $size={size}>
                      {user.subscriptionsCount?.toLocaleString() || 0}
                    </StatValue>
                    <StatLabel $size={size}>{CARDS.USER_STAT_SUBSCRIPTIONS}</StatLabel>
                  </Stat>
                )}
              </StatsContainer>
            )}
            
            {showActions && size !== 'compact' && actions.length > 0 && (
              <ActionsContainer $size={size} $layout={layout}>
                {actions.map((action, index) => (
                  <ActionButton
                    key={index}
                    $variant={action.variant || 'secondary'}
                    $size={size}
                    onClick={(e) => handleActionClick(action, e)}
                    disabled={action.disabled}
                  >
                    {action.icon && (
                      <VipIcon 
                        name={action.icon} 
                        size={size === 'large' ? 16 : 14} 
                      />
                    )}
                    {action.label}
                  </ActionButton>
                ))}
              </ActionsContainer>
            )}
          </UserInfo>
        </>
      )}
    </UserCardContainer>
  );
};

export default UserCard;
